import { useEffect, useMemo, useRef } from "react";
import Cookies from "js-cookie";
import {
  sendDoctorWebPresenceHeartbeat,
  sendDoctorWebPresenceOffline,
} from "../services/telemedicineApi";

const HEARTBEAT_INTERVAL_MS = 30000;
const HEARTBEAT_MIN_GAP_MS = 11000;
const SESSION_STORAGE_KEY = "doctor_web_presence_session_id";
const TAB_STORAGE_KEY = "doctor_web_presence_tab_id";
const LAST_HEARTBEAT_STORAGE_KEY = "doctor_web_presence_last_heartbeat_ts";

const generateId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getOrCreateStorageValue = (key) => {
  const existing = sessionStorage.getItem(key);
  if (existing) {
    return existing;
  }

  const newValue = generateId();
  sessionStorage.setItem(key, newValue);
  return newValue;
};

export const getDoctorWebPresenceSessionId = () => {
  return sessionStorage.getItem(SESSION_STORAGE_KEY) || "";
};

export const getOrCreateDoctorWebPresenceSessionId = () => {
  return getOrCreateStorageValue(SESSION_STORAGE_KEY);
};

export const getOrCreateDoctorWebPresenceTabId = () => {
  return getOrCreateStorageValue(TAB_STORAGE_KEY);
};

export const useDoctorWebPresenceHeartbeat = ({ enabled, isDoctorActive }) => {
  const intervalRef = useRef(null);
  const sessionIdRef = useRef(null);
  const isSendingHeartbeatRef = useRef(false);
  const lastHeartbeatAtRef = useRef(0);

  const shouldRun = enabled && isDoctorActive;

  const sessionId = useMemo(() => {
    if (!shouldRun) return null;
    return getOrCreateStorageValue(SESSION_STORAGE_KEY);
  }, [shouldRun]);

  const tabId = useMemo(() => {
    if (!shouldRun) return null;
    return getOrCreateStorageValue(TAB_STORAGE_KEY);
  }, [shouldRun]);

  useEffect(() => {
    if (!shouldRun || !sessionId || !tabId) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return undefined;
    }

    sessionIdRef.current = sessionId;

    const getLastHeartbeatAt = () => {
      if (lastHeartbeatAtRef.current > 0) {
        return lastHeartbeatAtRef.current;
      }

      const storedValue = Number(
        sessionStorage.getItem(LAST_HEARTBEAT_STORAGE_KEY) || "0",
      );
      if (Number.isFinite(storedValue) && storedValue > 0) {
        lastHeartbeatAtRef.current = storedValue;
        return storedValue;
      }

      return 0;
    };

    const markLastHeartbeat = (timestamp) => {
      lastHeartbeatAtRef.current = timestamp;
      sessionStorage.setItem(LAST_HEARTBEAT_STORAGE_KEY, timestamp.toString());
    };

    const sendHeartbeat = async (isVisible) => {
      const now = Date.now();
      const lastHeartbeatAt = getLastHeartbeatAt();
      if (now - lastHeartbeatAt < HEARTBEAT_MIN_GAP_MS) {
        return;
      }
      if (isSendingHeartbeatRef.current) {
        return;
      }

      isSendingHeartbeatRef.current = true;
      // Set optimistically to prevent duplicate calls from rapid remount/visibility events.
      markLastHeartbeat(now);

      try {
        await sendDoctorWebPresenceHeartbeat({
          session_id: sessionId,
          tab_id: tabId,
          is_visible: isVisible,
          page: window.location.pathname,
          client_ts: new Date().toISOString(),
        });
      } catch (error) {
        if (error?.status === 429 || error?.code === 429) return;
      } finally {
        isSendingHeartbeatRef.current = false;
      }
    };

    const sendOffline = async () => {
      try {
        await sendDoctorWebPresenceOffline({ session_id: sessionId });
      } catch (_) {}
    };

    const sendOfflineKeepalive = () => {
      try {
        const accessToken = Cookies.get("accessToken");
        if (!accessToken || !sessionIdRef.current) return;
        fetch(
          `${process.env.REACT_APP_API_PSEF}psef/doctors/me/web-presence/offline`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ session_id: sessionIdRef.current }),
            keepalive: true,
          },
        ).catch(() => {});
      } catch (_) {}
    };

    sendHeartbeat(document.visibilityState === "visible");

    intervalRef.current = setInterval(() => {
      sendHeartbeat(document.visibilityState === "visible");
    }, HEARTBEAT_INTERVAL_MS);

    const handleVisibilityChange = () => {
      sendHeartbeat(document.visibilityState === "visible");
    };
    const handlePageHide = () => {
      sendOfflineKeepalive();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      sendOffline();
    };
  }, [shouldRun, sessionId, tabId]);
};
